import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { cx } from 'class-variance-authority';
import { getImageProps } from 'next/image';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import type { ImageProps } from '@/app/components/Image';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';

import type { LandingPageMembershipTabContentFeaturedImageProps } from './LandingPageMembershipTabContentFeaturedImage';

export type LandingPageMembershipTabBackground =
  | 'blue'
  | 'gray'
  | 'green'
  | 'red';

export interface LandingPageMembershipTabData {
  backgroundColor: LandingPageMembershipTabBackground;
  Icon: SVGIconComponent;
  id: string;
  tab: {
    label: MessageKey;
  };
  tabPanel: {
    cards: Omit<LandingPageMembershipTabContentCardProps, 'backgroundColor'>[];
    featuredImage: Omit<
      LandingPageMembershipTabContentFeaturedImageProps,
      'Icon'
    >;
  };
}

export interface LandingPageMembershipTabContentCardProps {
  readonly backgroundColor: LandingPageMembershipTabBackground;
  readonly copy: MessageKey;
  readonly image: ImageProps;
  readonly newFeature?: boolean;
  readonly title: MessageKey;
}

const LandingPageMembershipTabContentCard = ({
  backgroundColor,
  copy,
  image,
  newFeature = false,
  title,
}: LandingPageMembershipTabContentCardProps) => {
  const t = useTranslations();

  const { props: backgroundImageProps } = getImageProps({
    alt: '',
    height: 300,
    src: `blue-sky/pop/pop_membership_card_bg_${backgroundColor}.png`,
    width: 300,
  });

  return (
    <div
      className="w-[285px] lg:w-[300px]"
      data-cy="landing-page-membership-card"
    >
      <div
        className="relative mb-6 flex aspect-square items-center justify-center overflow-hidden rounded-md bg-cover"
        data-cy="landing-page-membership-card-img"
        style={{ backgroundImage: `url(${backgroundImageProps.src})` }}
      >
        {newFeature && (
          <div className="absolute top-4 left-4 rounded-full border border-solid border-current bg-white px-2 py-1 text-[0.625rem] leading-none tracking-wider text-slate-500 uppercase opacity-80">
            {t('new')}
          </div>
        )}
        <Image
          data-cy="landing-page-membership-card-ui-img"
          format="png"
          fill={true}
          {...image}
          className={cx('object-contain p-6', image.className)}
        />
      </div>
      <Typography Element="h3" className="mb-3" variant="body-large">
        {t(title)}
      </Typography>
      <Typography variant="body-small">
        <RichText messageKey={copy} />
      </Typography>
    </div>
  );
};

export default LandingPageMembershipTabContentCard;
